import { useQuery } from "@apollo/client";
import { Dialog } from "@mui/material";
import { useState, useMemo, ReactElement } from "react";
import {
  useQueryParam,
  NumberParam,
  StringParam,
  withDefault,
} from "use-query-params";

import {
  CreateLinkShortenerDomainMutation,
  CreateLinkShortenerDomainMutationVariables,
  CreateLinkShortenerDomainRequestMutation,
  CreateLinkShortenerDomainRequestMutationVariables,
  LinkShortenerDomainStatus,
  ListLinkShortenerDomainsAndRequestsQuery,
  UpdateLinkShortenerDomainMutation,
  UpdateLinkShortenerDomainMutationVariables,
} from "~frontend/__generated__/graphql";
import ErrorCard from "~frontend/components/ErrorCard";
import Loading from "~frontend/components/Loading";
import { TableBox, TableControls } from "~frontend/components/Table";
import {
  CREATE_LINK_SHORTENER_DOMAIN,
  CREATE_LINK_SHORTENER_DOMAIN_REQUEST,
  LIST_DOMAINS_AND_REQUESTS,
  UPDATE_LINK_SHORTENER_DOMAIN,
} from "~frontend/graphql/domains";
import { useCurrentUserIsStaff } from "~frontend/providers/LoggedInUserProvider";
import useMutation from "~frontend/util/useMutation";

import AddDomainDialog from "./AddDomainDialog";
import RequestOrEditDomain from "./RequestOrEditDomain";
import ShortlinksTable from "./ShortlinksTable";
import UpdateDomainDialog from "./UpdateDomainDialog";
import {
  DOMAIN_FILTERS,
  DOMAIN_SORTS,
  DomainFilter,
  DomainSort,
} from "./constants";
import { DomainOrRequest, DomainRequest } from "./types";

function validateSort(sort: string): DomainSort {
  return DOMAIN_SORTS.find(s => s.key === sort)?.key ?? DOMAIN_SORTS[0].key;
}

function validateFilter(filter: string): DomainFilter {
  return (
    DOMAIN_FILTERS.find(s => s.key === filter)?.key ?? DOMAIN_FILTERS[0].key
  );
}

export default function ShortlinksList(): ReactElement {
  const [page, setPage] = useQueryParam("page", withDefault(NumberParam, 0));
  const [pageSize, setPageSize] = useQueryParam(
    "page_size",
    withDefault(NumberParam, 10)
  );
  const [sort, setSort] = useQueryParam(
    "sort",
    withDefault(StringParam, DOMAIN_SORTS[0].key)
  );
  const [filter, setFilter] = useQueryParam(
    "filter",
    withDefault(StringParam, DOMAIN_FILTERS[0].key)
  );
  const [search, setSearch] = useState("");

  const validatedSort = validateSort(sort);
  const validatedFilter = validateFilter(filter);

  const { loading, error, data } =
    useQuery<ListLinkShortenerDomainsAndRequestsQuery>(
      LIST_DOMAINS_AND_REQUESTS,
      { pollInterval: 30 * 1000 }
    );

  const [addDomainDialogOpen, toggleAddDomainDialog] = useState<{
    open: boolean;
    fulfillingRequestId?: string;
  }>({ open: false });

  const [requestingDomain, setRequestingDomain] = useState<boolean>(false);
  const [editingDomain, setEditingDomain] = useState<null | string>(null);

  const isStaff = useCurrentUserIsStaff();

  const [addDomain] = useMutation<
    CreateLinkShortenerDomainMutation,
    CreateLinkShortenerDomainMutationVariables
  >(CREATE_LINK_SHORTENER_DOMAIN, {
    refetchQueries: [LIST_DOMAINS_AND_REQUESTS],
    awaitRefetchQueries: true,
  });

  const [updateDomain] = useMutation<
    UpdateLinkShortenerDomainMutation,
    UpdateLinkShortenerDomainMutationVariables
  >(UPDATE_LINK_SHORTENER_DOMAIN, {
    onErrorSnackbarMessage: "Error updating domain",
    refetchQueries: [LIST_DOMAINS_AND_REQUESTS],
    awaitRefetchQueries: true,
  });

  const [requestDomain] = useMutation<
    CreateLinkShortenerDomainRequestMutation,
    CreateLinkShortenerDomainRequestMutationVariables
  >(CREATE_LINK_SHORTENER_DOMAIN_REQUEST, {
    onErrorSnackbarMessage: "Error requesting domain",
    refetchQueries: [LIST_DOMAINS_AND_REQUESTS],
    awaitRefetchQueries: true,
  });

  const filteredDomains = useMemo(() => {
    const allDomains = data?.listLinkShortenerDomains ?? [];

    const statusFiltered =
      validatedFilter === "active"
        ? allDomains.filter(
            d => d.status !== LinkShortenerDomainStatus.Archived
          )
        : allDomains.filter(
            d => d.status === LinkShortenerDomainStatus.Archived
          );

    const sorted: DomainOrRequest[] = statusFiltered.sort((a, b) => {
      switch (validatedSort) {
        case "created_at_asc":
          return a.createdAt - b.createdAt;
        case "created_at_desc":
          return b.createdAt - a.createdAt;
        case "domain_asc":
          return a.domain.localeCompare(b.domain);
        case "domain_desc":
          return b.domain.localeCompare(a.domain);
      }
    });

    if (validatedFilter === "active") {
      sorted.unshift(...(data?.listPendingLinkShortenerDomainRequests ?? []));
    }

    if (!search) {
      return sorted;
    }

    const lower = search.toLowerCase();
    return sorted.filter(d => d.domain.toLowerCase().includes(lower));
  }, [data, validatedSort, validatedFilter, search]);

  const rows = filteredDomains.slice(page * pageSize, (page + 1) * pageSize);

  if (error) {
    return <ErrorCard errors={[error]} />;
  }

  async function handleAddDomain(
    values: CreateLinkShortenerDomainMutationVariables
  ) {
    const { errors } = await addDomain({ variables: values });
    if (errors) {
      throw errors;
    } else {
      toggleAddDomainDialog({ open: false });
    }
  }

  async function handleEditDomain(
    values: UpdateLinkShortenerDomainMutationVariables
  ) {
    const { errors } = await updateDomain({ variables: values });
    if (errors) {
      throw errors;
    } else {
      setEditingDomain(null);
    }
  }

  async function handleRequestDomain(
    values: CreateLinkShortenerDomainRequestMutationVariables
  ) {
    const { errors } = await requestDomain({ variables: values });
    if (errors) {
      throw errors;
    } else {
      setRequestingDomain(false);
    }
  }

  async function handleRequestingClose() {
    setRequestingDomain(false);
  }

  let fulfillingRequest: null | DomainRequest = null;
  if (addDomainDialogOpen.fulfillingRequestId) {
    fulfillingRequest =
      data?.listPendingLinkShortenerDomainRequests.find(
        r => r.id === addDomainDialogOpen.fulfillingRequestId
      ) ?? null;
  }

  return (
    <>
      <TableControls
        addButtons={[
          {
            label: "Request Domain",
            onClick: () => setRequestingDomain(true),
            id: "request-domain",
          },
          ...(isStaff
            ? [
                {
                  label: "Import Domain",
                  onClick: () => toggleAddDomainDialog({ open: true }),
                  id: "add-domain",
                },
              ]
            : []),
        ]}
        sort={{
          value: sort,
          onChange: value => setSort(value as DomainSort),
          options: DOMAIN_SORTS.map(s => ({ value: s.key, label: s.label })),
        }}
        filters={[
          {
            id: "status",
            label: "Status",
            options: DOMAIN_FILTERS.map(f => ({
              value: f.key,
              label: f.label,
            })),
            value: validatedFilter,
            onChange: (value: string) => {
              setPage(0);
              setFilter(value as DomainFilter);
            },
          },
        ]}
        search={{
          value: search,
          onChange: value => {
            setSearch(value);
            setPage(0);
          },
          placeholder: "Search for a domain or subaccount",
        }}
      />
      <TableBox sx={{ pb: 1 }}>
        {loading ? (
          <Loading />
        ) : (
          <ShortlinksTable
            rows={rows}
            page={page}
            handleChangePage={setPage}
            rowsPerPage={pageSize}
            handleChangeRowsPerPage={newRowsPerPage => {
              setPageSize(newRowsPerPage);
              setPage(0);
            }}
            totalCount={filteredDomains.length}
            handleEditDomain={setEditingDomain}
            handleFulfillRequest={requestId =>
              toggleAddDomainDialog({
                open: true,
                fulfillingRequestId: requestId,
              })
            }
          />
        )}
      </TableBox>
      <AddDomainDialog
        open={addDomainDialogOpen.open}
        onClose={() => toggleAddDomainDialog({ open: false })}
        fulfillingRequest={fulfillingRequest}
        onSubmit={handleAddDomain}
      />
      <Dialog
        open={requestingDomain}
        onClose={handleRequestingClose}
        maxWidth="sm"
        fullWidth
      >
        <RequestOrEditDomain
          open={requestingDomain}
          onClose={handleRequestingClose}
          onSubmitRequest={handleRequestDomain}
        />
      </Dialog>
      <UpdateDomainDialog
        open={editingDomain !== null}
        onClose={() => {
          setEditingDomain(null);
        }}
        onSubmitEdit={handleEditDomain}
        domain={
          editingDomain
            ? (data?.listLinkShortenerDomains?.find(
                d => d.id === editingDomain
              ) ?? null)
            : null
        }
      />
    </>
  );
}
